// SPDX-License-Identifier: GPL-3.0-or-later
// Touch input of the game page (THEME.md section 11.2).
// Left zone = left flipper. Right zone = right flipper. Plunger button: hold to pull, release to
// launch. Nudge button and a two-finger tap on the table both nudge. A finger on a zone,
// a button or the draft panel never counts as a table finger. Every target is 44 px or more
// (the CSS sets the size; `MIN_TOUCH_TARGET_PX` is the number that the test checks).

import type { InputAction, InputEvent } from "../src/shared/contracts.ts";
import { insideDraftPanel } from "./touch-surface.ts";

/** Smallest touch target side, in CSS pixels. */
export const MIN_TOUCH_TARGET_PX = 44;

/** The part of a pointer event that this module reads. */
export interface PointerEventLike {
  readonly pointerId: number;
  readonly pointerType?: string;
  /** The element that got the event first. The table handler reads it. */
  readonly target?: unknown;
  preventDefault(): void;
  /** Stops the event from reaching the parent elements. */
  stopPropagation?(): void;
}

/** True when the event started inside the touch controls (a zone or a button) or the draft panel. */
function startedInControls(ev: PointerEventLike): boolean {
  const node = ev.target as { closest?: (selector: string) => unknown } | null | undefined;
  const onControl = typeof node?.closest === "function" && node.closest(".touch") !== null;
  // A finger on a draft card is not a table finger. Two fingers on the cards must not nudge.
  return onControl || insideDraftPanel(ev.target);
}

/** The part of an element that this module uses. */
export interface PointerTarget {
  addEventListener(type: string, listener: (ev: PointerEventLike) => void): void;
  removeEventListener(type: string, listener: (ev: PointerEventLike) => void): void;
  setPointerCapture?(pointerId: number): void;
}

/** The zones of the touch controls. */
export interface TouchZones {
  readonly left: PointerTarget;
  readonly right: PointerTarget;
  readonly plunger: PointerTarget;
  readonly nudge: PointerTarget;
  /** The table area. Two fingers down at once nudge. Optional. */
  readonly table?: PointerTarget;
}

/** A handle to remove every listener and to release every held action. */
export interface TouchController {
  releaseAll(): void;
  dispose(): void;
}

/**
 * Bind the zones. A zone presses its action on pointer down and releases it when the last
 * pointer of that zone goes up or is cancelled. Two fingers on the table send one nudge.
 */
export function createTouchInput(
  zones: TouchZones,
  send: (ev: InputEvent) => void,
): TouchController {
  const core: TouchCore = { send, cleanups: [], active: new Map<InputAction, Set<number>>() };
  bindHeld(core, zones.left, "left_flipper");
  bindHeld(core, zones.right, "right_flipper");
  bindHeld(core, zones.plunger, "plunger");
  bindNudge(core, zones.nudge);
  if (zones.table !== undefined) bindTableFingers(core, zones.table);
  return {
    releaseAll: () => releaseAllTouch(core),
    dispose() {
      releaseAllTouch(core);
      for (const cleanup of core.cleanups) cleanup();
      core.cleanups.length = 0;
    },
  };
}

/** The state of one touch controller. */
interface TouchCore {
  readonly send: (ev: InputEvent) => void;
  readonly cleanups: Array<() => void>;
  /** The pointers that hold each action. */
  readonly active: Map<InputAction, Set<number>>;
}

type PointerHandler = (ev: PointerEventLike) => void;

/** Listen for events of one target. The cleanup removes the listeners. */
function listen(
  core: TouchCore,
  target: PointerTarget,
  types: readonly string[],
  handler: PointerHandler,
): void {
  for (const type of types) target.addEventListener(type, handler);
  core.cleanups.push(() => {
    for (const type of types) target.removeEventListener(type, handler);
  });
}

function press(core: TouchCore, action: InputAction, id: number): void {
  const ids = core.active.get(action) ?? new Set<number>();
  const first = ids.size === 0;
  ids.add(id);
  core.active.set(action, ids);
  if (first) core.send({ action, pressed: true });
}

function lift(core: TouchCore, action: InputAction, id: number): void {
  const ids = core.active.get(action);
  if (ids === undefined || !ids.delete(id)) return;
  if (ids.size === 0) {
    core.active.delete(action);
    core.send({ action, pressed: false });
  }
}

function releaseAllTouch(core: TouchCore): void {
  for (const action of [...core.active.keys()]) {
    core.active.delete(action);
    core.send({ action, pressed: false });
  }
}

/**
 * Keep the finger on this zone until it lifts, also when it slides over the midline.
 * A browser can refuse the capture (the pointer is already gone): the press still counts.
 */
function capture(target: PointerTarget, pointerId: number): void {
  try {
    target.setPointerCapture?.(pointerId);
  } catch {
    // The pointer ended before the capture. The pointerup that follows lifts the action.
  }
}

/** A zone presses its action on pointer down and lifts it when the pointer ends. */
function bindHeld(core: TouchCore, target: PointerTarget, action: InputAction): void {
  listen(core, target, ["pointerdown"], (ev) => {
    ev.preventDefault();
    // The zones sit inside the table. A finger on a zone must not count as a table finger.
    ev.stopPropagation?.();
    capture(target, ev.pointerId);
    press(core, action, ev.pointerId);
  });
  listen(core, target, ["pointerup", "pointercancel", "lostpointercapture"], (ev) =>
    lift(core, action, ev.pointerId),
  );
}

function bindNudge(core: TouchCore, target: PointerTarget): void {
  listen(core, target, ["pointerdown"], (ev) => {
    ev.preventDefault();
    ev.stopPropagation?.();
    core.send({ action: "nudge_up", pressed: true });
  });
}

/** Two fingers on the table nudge once. A finger on a zone or a button is not a table finger. */
function bindTableFingers(core: TouchCore, table: PointerTarget): void {
  const fingers = new Set<number>();
  let nudged = false;
  listen(core, table, ["pointerdown"], (ev) => {
    if (ev.pointerType !== undefined && ev.pointerType !== "touch") return;
    if (startedInControls(ev)) return;
    fingers.add(ev.pointerId);
    if (fingers.size === 2 && !nudged) {
      nudged = true;
      core.send({ action: "nudge_up", pressed: true });
    }
  });
  listen(core, table, ["pointerup", "pointercancel"], (ev) => {
    fingers.delete(ev.pointerId);
    if (fingers.size === 0) nudged = false;
  });
}
